import { FunctionComponent, useState } from "react";
import Select, { MultiValue } from "react-select";
import { SkyQTvGuide, SkyQTvGuideProgram, TVChannel, searchSkyTvGuide } from "../../../api/tvChannelApi";
import {
    CreateScheduledPriorityListStreamRequest, 
    ScheduledPriorityList, 
    SourceType,
    createScheduledListStreamsBulk, 
    getScheduledListStreams,
} from "../../../api/priorityListApi";
import { getPastDateByDaysAgo } from "../../../services/dateService";
import { useErrorHandlingStore } from "../../../hooks/useErrorHandling";
import { Button } from "../../components/Button";
import GlobalSpinner from "../../components/globalsSpinner/GlobalSpinner";
import { SmallModal } from "../../components/sideModal/Modal";
import InnerLayout from "../innerLayout/InnerLayout";
import ChannelSearchFilters from "./skyTvGuide/ChannelSearchFilters";
import EditStreamModal from "./skyTvGuide/EditStreamModal";
import OverlapConfirmModal from "./skyTvGuide/OverlapConfirmModal";
import PriorityListSettingsBlock from "./skyTvGuide/PriorityListSettingsBlock";
import PriorityStreamsTable from "./skyTvGuide/PriorityStreamsTable";
import TvGuideGrid from "./skyTvGuide/TvGuideGrid";
import { OverlapPair, PerListOverlap, PriorityStreamPreview } from "./skyTvGuide/types";
import { findNewVsExistingOverlaps, findNewVsNewOverlaps } from "./skyTvGuide/utils";
import { useListSettings } from "./skyTvGuide/useListSettings";
import { useSkyTvGuideData } from "./skyTvGuide/useSkyTvGuideData";
import { useStreamEdit } from "./skyTvGuide/useStreamEdit";
import "./SkyTvGuide.css";

interface SkyTvGuideProps {
    logOut: () => void;
    toogleCollapse: () => void;
    isCollapsed: boolean;
}

const SkyTvGuide: FunctionComponent<SkyTvGuideProps> = (props) => {
    const { priorityLists, tvChannelsList, tags } = useSkyTvGuideData();

    const [items, setItems] = useState<SkyQTvGuide[]>([]);
    const [priorityStreams, setPriorityStreams] = useState<PriorityStreamPreview[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [selectedChannels, setSelectedChannels] = useState<TVChannel[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>(getPastDateByDaysAgo(0));
    const [programName, setProgramName] = useState<string>("");

    const [selectedPriorityLists, setSelectedPriorityLists] = useState<ScheduledPriorityList[]>([]);
    const { listSettings, updateListSettings } = useListSettings(selectedPriorityLists);

    const streamEdit = useStreamEdit(setPriorityStreams, tvChannelsList);

    const [showProcessConfirm, setShowProcessConfirm] = useState<boolean>(false);
    const [showOverlapConfirm, setShowOverlapConfirm] = useState<boolean>(false);
    const [newVsNewOverlaps, setNewVsNewOverlaps] = useState<OverlapPair[]>([]);
    const [newVsExistingOverlaps, setNewVsExistingOverlaps] = useState<PerListOverlap[]>([]);

    const fetchData = async () => {
        if (selectedChannels.length === 0) return;
        setIsLoading(true);
        const result = await searchSkyTvGuide({
            tvChannelIds: selectedChannels.map(c => c.id),
            date: selectedDate || undefined,
            programName: programName || undefined,
        });
        if (result.type === 'succes') setItems(result.data.items);
        setIsLoading(false);
    };

    const selectProgram = (channel: SkyQTvGuide, program: SkyQTvGuideProgram) => {
        const id = `${channel.tvChannelId}-${program.startTime}-${program.endTime}`;
        if (priorityStreams.some(s => s.id === id)) {
            setPriorityStreams(prev => prev.filter(s => s.id !== id));
            return;
        }
        setPriorityStreams(prev => [...prev, {
            id,
            tvChannelId: channel.tvChannelId,
            tvChannelName: channel.tvChannelName,
            startTime: program.startTime,
            endTime: program.endTime,
            programName: program.name,
            shortDescription: program.shortDescription,
            fullDescription: program.fullDescription,
            sourceType: SourceType.CapturedStream,
            rtmpUrl: '',
            series: '', season: '', episode: '', title: '',
            iabCat: '', genre: '', rating: '',
        }]);
    };

    const removeSelectedItem = (item: PriorityStreamPreview) => {
        setPriorityStreams(prev => prev.filter(i => i.id !== item.id));
    };

    const onProcessClick = async () => {
        if (selectedPriorityLists.length === 0) return;
        setIsLoading(true);

        const newVsNew = findNewVsNewOverlaps(priorityStreams);

        const perListOverlaps: PerListOverlap[] = [];
        for (const list of selectedPriorityLists) {
            const result = await getScheduledListStreams(list.id);
            const existingStreams = result.type === 'succes' ? result.data.items : [];
            const overlaps = findNewVsExistingOverlaps(priorityStreams, existingStreams);
            if (overlaps.length > 0) perListOverlaps.push({ listName: list.name, overlaps });
        }

        setIsLoading(false);
        if (newVsNew.length > 0 || perListOverlaps.length > 0) {
            setNewVsNewOverlaps(newVsNew);
            setNewVsExistingOverlaps(perListOverlaps);
            setShowOverlapConfirm(true);
        } else {
            setShowProcessConfirm(true);
        }
    };

    const handleProcess = async () => {
        if (selectedPriorityLists.length === 0) return;
        setIsLoading(true);

        const entries = listSettings.flatMap(settings =>
            priorityStreams.map(stream => ({
                listId: settings.listId,
                request: {
                    name: stream.programName,
                    startTime: stream.startTime,
                    endTime: stream.endTime,
                    isNestedListEnabled: settings.isUseNestedPlaylist,
                    showNestedListEachMinutes: +settings.nestedPlaylistPeriod,
                    nestedPlayListId: settings.selectedNestedPlaylist?.value || null,
                    tvChannelId: stream.tvChannelId,
                    includeTagIds: settings.includedTags.map(it => it.id),
                    excludeTagIds: settings.excludedTags.map(it => it.id),
                    useORRelationshipForIncludeTags: settings.isUseOrRelationship,
                    preRollPlaylistId: settings.selectedPreRollPlaylist?.value || null,
                    postRollPlaylistId: settings.selectedPostRollPlaylist?.value || null,
                    series: stream.series,
                    season: stream.season,
                    episode: stream.episode,
                    iabCat: stream.iabCat,
                    genre: stream.genre,
                    rating: stream.rating,
                    title: stream.title,
                    rtmpUrl: '',
                    sourceType: SourceType.CapturedStream,
                } as CreateScheduledPriorityListStreamRequest,
            }))
        );

        const result = await createScheduledListStreamsBulk({
            streams: entries,
        });

        if (result.type === 'succes') {
            setPriorityStreams([]);
        } else {
            useErrorHandlingStore.getState().showModal("Error", "Failed to process streams.");
        }
        setIsLoading(false);
    };

    const selectedProgramIds = priorityStreams.map(s => s.id);

    return (
        <InnerLayout
            isCollapsed={props.isCollapsed}
            logOut={props.logOut}
            toogleCollapse={props.toogleCollapse}
            activeTab="priorityList">
            <>
                <div className="d-flex justify-content-between align-items-center mb-20 pt-50">
                    <h2>Sky TV Guide</h2>
                </div>

                <div className="d-flex align-items-end gap15 mb-20">
                    <div style={{ minWidth: 400 }}>
                        <label className="form-label">Priority Lists</label>
                        <Select
                            classNamePrefix="select"
                            isMulti={true}
                            isClearable={true}
                            options={priorityLists}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            onChange={(v: MultiValue<ScheduledPriorityList>) => setSelectedPriorityLists([...v])}
                            value={selectedPriorityLists}
                            placeholder="Select priority lists..."
                        />
                    </div>
                </div>

                <ChannelSearchFilters
                    tvChannelsList={tvChannelsList}
                    selectedChannels={selectedChannels}
                    setSelectedChannels={setSelectedChannels}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    programName={programName}
                    setProgramName={setProgramName}
                    onSearch={fetchData}
                />

                {items.length > 0 ? (
                    <TvGuideGrid
                        channels={items}
                        selectedProgramIds={selectedProgramIds}
                        onSelectProgram={selectProgram}
                    />
                ) : (
                    !isLoading && <p className="w-100 text-center">Please select channels and click Search</p>
                )}

                {priorityStreams.length > 0 && (
                    <>
                        {listSettings.map(settings => (
                            <PriorityListSettingsBlock
                                key={settings.listId}
                                settings={settings}
                                tags={tags}
                                onUpdate={updateListSettings}
                            />
                        ))}
                        <PriorityStreamsTable
                            streams={priorityStreams}
                            onEdit={streamEdit.openEditModal}
                            onRemove={removeSelectedItem}
                            onProcess={onProcessClick}
                        />
                    </>
                )}

                <EditStreamModal
                    show={streamEdit.showEditModal}
                    onClose={streamEdit.closeEditModal}
                    onSave={streamEdit.saveEditStream}
                    tvChannelsList={tvChannelsList}
                    fields={streamEdit.fields}
                />

                <SmallModal show={showProcessConfirm} onRequestClose={() => setShowProcessConfirm(false)}>
                    <p>{priorityStreams.length} priority stream{priorityStreams.length !== 1 ? 's' : ''} will be created for {listSettings.length} list{listSettings.length !== 1 ? 's' : ''}. Continue?</p>
                    <div className="modal-btn-group">
                        <Button color="gray" label="Cancel" onClick={() => setShowProcessConfirm(false)} />
                        <Button color="green" label="Yes" onClick={() => { setShowProcessConfirm(false); handleProcess(); }} />
                    </div>
                </SmallModal>

                <OverlapConfirmModal
                    show={showOverlapConfirm}
                    onClose={() => setShowOverlapConfirm(false)}
                    onConfirm={() => { setShowOverlapConfirm(false); setShowProcessConfirm(true); }}
                    newVsNewOverlaps={newVsNewOverlaps}
                    newVsExistingOverlaps={newVsExistingOverlaps}
                />

                <GlobalSpinner isShow={isLoading} />
            </>
        </InnerLayout>
    );
};

export default SkyTvGuide;
